import { PublicClientApplication } from '@azure/msal-browser';

/**
 * Work and school accounts only.
 *
 * Deliberately `organizations` rather than `common`. `common` also offers
 * personal Microsoft accounts (outlook.com, hotmail.com), which can never
 * resolve here: sign-in requires the token's tenant to match a row in
 * `organization_idp_tenants`, and a personal account has no such directory. The
 * backend would refuse them, but only after the person had already signed in to
 * Microsoft and been redirected back, which is the worst moment to fail. This
 * keeps them out of the account picker in the first place.
 */
const AUTHORITY = 'https://login.microsoftonline.com/organizations';

let instance: PublicClientApplication | null = null;
let initialisedFor: string | null = null;

/**
 * The MSAL client, created once per client id.
 *
 * The client id comes from `GET /auth/social/providers` rather than the build,
 * matching how Google and Apple are wired: configuration changes with a deploy
 * of the backend, not a rebuild of the frontend, and nothing about it is secret.
 *
 * `initialize()` is mandatory in MSAL v4+ and must finish before any other call,
 * so it is awaited here rather than left to each caller to remember.
 */
export async function getMsal(clientId: string): Promise<PublicClientApplication> {
  if (instance && initialisedFor === clientId) return instance;

  const created = new PublicClientApplication({
    auth: {
      clientId,
      authority: AUTHORITY,
      redirectUri: `${window.location.origin}/auth/microsoft`,
    },
    cache: {
      // sessionStorage, not localStorage: MSAL's cache holds tokens for a system
      // that fronts patient records, and a shared clinic workstation should not
      // carry them into the next person's session.
      cacheLocation: 'sessionStorage',
    },
  });

  await created.initialize();
  instance = created;
  initialisedFor = clientId;
  return created;
}

/**
 * What we ask Entra for.
 *
 * `openid` and `profile` only. No Graph scopes: this app never calls Microsoft
 * on the user's behalf, it only needs to know who they are. Asking for more
 * would put a consent prompt in front of every practice administrator for
 * access we would never use.
 */
export const LOGIN_REQUEST = { scopes: ['openid', 'profile', 'email'] };
